// Donor Directory Vue Component
const DonorDirectory = {
  props: {
    fundraiserId: {
      type: String,
      default: null
    },
    columns: {
      type: String,
      default: '3'
    }
  },

  setup(props) {
    const { computed, onMounted, watch } = Vue;
    const { donors, loading, error, loadDonors, getDonorsByFundraiser } = window.donorStore;
    const giftAidLogoUrl = window.giftAidLogoUrl || '';

    // Get filtered donors based on fundraiser
    const filteredDonors = computed(() => {
      if (!props.fundraiserId) return donors.value;
      return donors.value.filter(donor => donor.fundraiser_id === props.fundraiserId);
    });

    const donorCount = computed(() => filteredDonors.value.length);

    // Helper functions
    const formatMoney = (amount) => {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP'
      }).format(amount);
    };

    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { 
        day: 'numeric', 
        month: 'short', 
        year: 'numeric' 
      });
    };

    const getInitials = (name) => {
      if (!name) return '';
      return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase();
    };

    const getAvatarHtml = (donor) => {
      if (donor.anonymous === 'true') {
        return { type: 'initials', content: 'A' };
      } else if (donor.avatar) {
        return { type: 'image', content: donor.avatar, alt: donor.name || 'Donor' };
      } else if (donor.company) {
        const parts = donor.company.split(' ');
        const initials = (parts[0]?.charAt(0) || '') + (parts[1]?.charAt(0) || '');
        return { type: 'initials', content: initials.toUpperCase() };
      } else {
        return { type: 'initials', content: getInitials(donor.name) };
      }
    };

    const getDisplayName = (donor) => {
      if (donor.anonymous === 'true') {
        return { name: 'Anonymous', onBehalf: null };
      } else if (donor.company) {
        return { name: donor.company, onBehalf: donor.name };
      } else {
        return { name: donor.name || 'Anonymous', onBehalf: null };
      }
    };

    const getGiftAidInfo = (donor) => {
      if (donor.gift_aid === 'true') {
        const donationDate = new Date(donor.date);
        const april16_2025 = new Date('2025-04-16');
        
        if (donationDate >= april16_2025) {
          const giftAidAmount = (parseFloat(donor.amount) * 0.25).toFixed(2);
          return { show: true, amount: giftAidAmount };
        }
      }
      return { show: false, amount: 0 };
    };

    const AVATAR_TRANSFORM = 'w=96&h=96&fit=crop&crop=faces&auto=format,compress&q=65&dpr=2';
    const DEFAULT_FUNDRAISER_PICTURE = 'https://raisely-images.imgix.net/66aa8650-95dc-11ef-83ff-893a78ecbdcb/uploads/default-avatar-jpg-92fdf7.jpg?' + AVATAR_TRANSFORM;

    const getFundraiserPicture = (donor) => {
      if (!donor.fundraiser_picture) return DEFAULT_FUNDRAISER_PICTURE;
      const separator = donor.fundraiser_picture.includes('?') ? '&' : '?';
      return donor.fundraiser_picture + separator + AVATAR_TRANSFORM;
    };

    const getTributeComment = (donor) => {
      const result = { tribute: null, comment: null };
      
      if (donor.tribute) {
        const dedicatedPrefix = donor.tribute.match(/^(Memory|memory|Dedicated|dedicated|For|for)/) ? '' : 'Dedicated to ';
        result.tribute = dedicatedPrefix + donor.tribute;
      }
      
      if (donor.comment) {
        result.comment = donor.comment
          .replace(/profanity/g, '')
          .replace(/https?:\/\//g, '');
      }
      
      return result;
    };

    // Update external filteredCount element when donor count changes
    const updateExternalCount = () => {
      const externalElement = document.getElementById('filteredCount');
      if (externalElement) {
        if (loading.value) {
          externalElement.textContent = 'Loading...';
        } else if (error.value) {
          externalElement.textContent = 'Error loading';
        } else {
          externalElement.textContent = `${donorCount.value} Results`;
        }
      }
    };

    // Watch for changes in donor count, loading, or error state
    watch([donorCount, loading, error], () => {
      updateExternalCount();
    }, { immediate: true });

    // Initialize
    onMounted(() => {
      loadDonors();
    });

    return {
      filteredDonors,
      donorCount,
      loading,
      error,
      formatMoney,
      formatDate,
      getAvatarHtml,
      getDisplayName,
      getGiftAidInfo,
      getTributeComment,
      getFundraiserPicture,
      giftAidLogoUrl
    };
  },

  template: `
    <div>
      <!-- Donor List -->
      <div class='support-directory__list donors' :class="'grid-columns-' + columns" id='donorsList'>
        <div v-if="loading" class='loading-message'>Loading donors...</div>
        <div v-else-if="error" class='error-message'>Error loading donors. Please try again.</div>
        <div v-else-if="filteredDonors.length === 0" class='no-donors-message'>No donors found.</div>
        
        <div v-else class='donor-card' v-for="donor in filteredDonors" :key="donor.id">
          <quote-component class='quote'>
            <max-lines-component class='quote__content donor'>  
              <div class='donor-card-wrapper'>
                <div class='donor-avatar'>
                  <img v-if="getAvatarHtml(donor).type === 'image'" 
                       :src="getAvatarHtml(donor).content" 
                       :alt="getAvatarHtml(donor).alt" 
                       loading="lazy">
                  <span v-else class='donor-initals'>{{ getAvatarHtml(donor).content }}</span>
                </div>
                
                <div class='donor-card-details'>
                  <div class='quote__author-name'>
                    {{ getDisplayName(donor).name }}
                    <span v-if="getDisplayName(donor).onBehalf" class='donor-behalf'>
                      on behalf of {{ getDisplayName(donor).onBehalf }}
                    </span>
                  </div>
                  
                  <div class='quote__text quote__text--medium'>
                    <span class='donor-amount'>
                      {{ formatMoney(parseFloat(donor.amount)) }}
                      <template v-if="getGiftAidInfo(donor).show">
                        + <span class="gift-aid-text">£{{ getGiftAidInfo(donor).amount }}</span>
                        <span class="gift-aid-logo">
                          <img :src="giftAidLogoUrl" alt="Gift Aid" width="60" height="20">
                        </span>
                      </template>
                    </span>
                  </div>
                  
                  <div v-if="getTributeComment(donor).tribute || getTributeComment(donor).comment" class='donor-messaging'>
                    <p v-if="getTributeComment(donor).tribute" class='donor-tribute'>
                      {{ getTributeComment(donor).tribute }}
                    </p>
                    <q v-if="getTributeComment(donor).comment" class='donor-comment'>
                      {{ getTributeComment(donor).comment }}
                    </q>
                  </div>
                  
                  <p class='donor-date'>
                    {{ formatDate(donor.date) }}
                  </p>

                  <a v-if="donor.fundraiser_link" class="fundraiser-details" :href="donor.fundraiser_link" target="_blank">
                    <img class="fundraiser-avatar" :src="getFundraiserPicture(donor)" width="64px" height="64px" alt="{{ donor.fundraiser }} Profile Picture" />
                    <span class="fundraiser-name">{{ donor.fundraiser }}</span>
                  </a>
                  <span v-if="donor.fundraiser_link" class="fundraiser-donation">{{ donor.campaign }} Fundraiser</span>
                </div>
              </div>
            </max-lines-component>
          </quote-component>
        </div>
      </div>
    </div>
  `
};

// Auto-mount donor directory components
document.addEventListener('DOMContentLoaded', () => {
  const { createApp } = Vue;
  const donorDirectories = document.querySelectorAll('.donor-directory-vue');
  
  donorDirectories.forEach(element => {
    const fundraiserId = element.getAttribute('data-fundraiser-id');
    const columns = element.getAttribute('data-columns') || '3';
    
    createApp(DonorDirectory, {
      fundraiserId,
      columns
    }).mount(element);
  });
});